import { LegalPageLayout } from '@/components/layout/LegalPageLayout';
import { Link } from 'react-router-dom';
import { LEGAL_SUITE_VERSION, LEGAL_SUITE_LAST_UPDATED_LABEL } from '@/constants/legalSuite';

export default function ConsentInformation() {
  return (
    <LegalPageLayout
      title="Información sobre Consentimiento"
      lastUpdated={LEGAL_SUITE_LAST_UPDATED_LABEL}
      version={LEGAL_SUITE_VERSION}
    >
      <nav className="bg-muted/50 rounded-lg p-4 sm:p-6 mb-8 border">
        <p className="font-semibold text-foreground mb-3">Contenido</p>
        <ol className="!text-sm space-y-1">
          <li><a href="#datos-tratados">Qué datos serán tratados</a></li>
          <li><a href="#finalidad">Finalidad del tratamiento</a></li>
          <li><a href="#que-implica">Qué implica aceptar</a></li>
          <li><a href="#derechos">Derechos del usuario</a></li>
          <li><a href="#revocar">Cómo revocar el consentimiento</a></li>
          <li><a href="#referencia">Referencia a la Política de Privacidad</a></li>
        </ol>
      </nav>

      <p>
        Este documento te informa de manera clara y transparente sobre el tratamiento de tus datos personales en la
        plataforma <strong>FOCO (Tu Dinero, Tu Poder)</strong>, para que puedas otorgar un consentimiento verdaderamente
        informado antes de crear tu cuenta. El texto que aceptas en el registro corresponde al consentimiento unificado
        versión <strong>{LEGAL_SUITE_VERSION}</strong> (misma fecha de vigencia que arriba).
      </p>

      <h2 id="datos-tratados">1. Qué datos serán tratados</h2>
      <p>Al utilizar FOCO, se recopilarán y procesarán los siguientes datos:</p>
      <ul>
        <li><strong>Datos de identificación:</strong> nombre completo y dirección de correo electrónico.</li>
        <li><strong>Credenciales de acceso:</strong> contraseña (almacenada de forma cifrada; nunca en texto plano).</li>
        <li><strong>Datos financieros personales:</strong> ingresos, gastos fijos y variables, ahorros, inversiones, pagos de deudas, metas financieras y presupuestos.</li>
        <li><strong>Información de deudas:</strong> nombre de la deuda, acreedor, saldo, tasa de interés y pagos mínimos.</li>
        <li><strong>Historial de interacciones:</strong> mensajes con el asistente financiero de IA.</li>
        <li><strong>Datos técnicos:</strong> dirección IP, navegador, sistema operativo (para fines de seguridad).</li>
        <li>
          <strong>Archivos cargados:</strong> estados de cuenta en <strong>PDF</strong>; la importación asistida por IA
          puede enviar el archivo a <strong>OpenAI</strong> (ver Política de privacidad).
        </li>
        <li>
          <strong>Datos de suscripción (si contratas Premium):</strong> plan, estado de pago, fechas e identificadores
          en <strong>Stripe</strong>; FOCO no recibe el número completo de tu tarjeta.
        </li>
      </ul>

      <h2 id="finalidad">2. Finalidad del tratamiento</h2>
      <p>Tus datos se utilizarán exclusivamente para:</p>
      <ul>
        <li>Brindar el servicio de gestión financiera personal (registro de transacciones, seguimiento de presupuestos, gestión de deudas).</li>
        <li>Calcular tu Puntaje FOCO y generar análisis de tu salud financiera.</li>
        <li>Generar recomendaciones personalizadas mediante inteligencia artificial.</li>
        <li>
          Enviar alertas y notificaciones (presupuestos, deudas, metas) cuando tengas plan{' '}
          <strong>Premium</strong> o rol de administrador y hayas activado las preferencias en Configuración.
        </li>
        <li>Gestionar tu plan gratuito o de pago y, si contratas Premium, procesar el cobro con <strong>Stripe</strong>.</li>
        <li>Generar reportes financieros mensuales y anuales para tu consulta.</li>
        <li>Permitir la administración de tu cuenta por parte del equipo de coaching financiero (si aplica).</li>
        <li>Mejorar la plataforma y sus funcionalidades.</li>
      </ul>

      <h2 id="que-implica">3. Qué implica aceptar</h2>
      <p>Al otorgar tu consentimiento, autorizas a FOCO a:</p>
      <ul>
        <li>Almacenar y procesar tus datos personales y financieros en servidores seguros en la nube.</li>
        <li>Utilizar tus datos financieros para generar cálculos, indicadores y proyecciones dentro de la plataforma.</li>
        <li>
          Enviar tus consultas y resúmenes financieros al asistente de IA (<strong>OpenAI</strong>) para
          recomendaciones y tips personalizados.
        </li>
        <li>
          Enviar comunicaciones por correo (alertas Premium si aplica, reportes, seguridad, avisos de suscripción o
          pago fallido).
        </li>
        <li>
          Procesar PDFs que subas; si usas la opción con IA, enviar el documento a <strong>OpenAI</strong> para
          sugerir movimientos.
        </li>
      </ul>
      <p>
        <strong>Importante:</strong> el consentimiento del registro es distinto de la <strong>contratación de Premium</strong>.
        Puedes usar FOCO gratis; al pagar aceptas también los{' '}
        <Link to="/terms-and-conditions" className="text-primary underline">
          Términos y Condiciones
        </Link>{' '}
        (suscripciones) y las políticas de Stripe. Varias funciones (chat IA, metas, importaciones ilimitadas, alertas
        por correo de ciertos módulos) requieren Premium o rol de administrador.
      </p>

      <h2 id="derechos">4. Derechos del usuario</h2>
      <p>En todo momento tienes derecho a:</p>
      <ul>
        <li><strong>Acceder</strong> a una copia completa de los datos personales que FOCO tiene sobre ti.</li>
        <li><strong>Rectificar</strong> cualquier dato incorrecto o incompleto.</li>
        <li><strong>Eliminar</strong> tus datos personales (derecho al olvido), solicitando la eliminación de tu cuenta.</li>
        <li><strong>Portar</strong> tus datos: exportar tu información financiera en formatos legibles.</li>
        <li><strong>Oponerte</strong> al tratamiento de ciertos datos.</li>
        <li><strong>Revocar</strong> tu consentimiento en cualquier momento, sin que esto afecte la licitud del tratamiento previo.</li>
      </ul>

      <h2 id="revocar">5. Cómo revocar el consentimiento</h2>
      <p>Puedes revocar tu consentimiento de las siguientes formas:</p>
      <ul>
        <li><strong>Desde la plataforma:</strong> ve a Configuración → Privacidad y Cookies y modifica tus preferencias de consentimiento.</li>
        <li><strong>Por correo electrónico:</strong> escribe a info@premium-advisor.com indicando tu nombre y correo registrado.</li>
        <li><strong>Eliminación de cuenta:</strong> solicita la eliminación completa de tu cuenta y todos los datos asociados.</li>
      </ul>
      <p>
        La revocación del consentimiento no afecta la licitud del tratamiento basado en el consentimiento
        previo a su retirada. Una vez revocado, ciertas funcionalidades de la plataforma pueden dejar de
        estar disponibles.
      </p>

      <h2 id="referencia">6. Referencia a la Política de Privacidad</h2>
      <p>
        Para información detallada sobre cómo se protegen, almacenan y comparten tus datos, consulta nuestra{' '}
        <Link to="/privacy-policy" className="text-primary underline">Política de Privacidad completa</Link> y la{' '}
        <Link to="/cookies-policy" className="text-primary underline">Política de Cookies</Link>.
      </p>
      <p>
        Si tienes dudas o necesitas más información, escribe a <strong>info@premium-advisor.com</strong>.
      </p>
    </LegalPageLayout>
  );
}
